import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import {
  USER_CAMPAIGN_KEY,
  USER_CONTENT_KEY,
  USER_MEDIUM_KEY,
  USER_SOURCE_KEY,
} from "@hopper-b2b/utilities";
import { useUserTrackingProperties } from "@/hooks/shared/useUserTrackingProperties";
import { ParamToKey } from "@/utilities/tracking";

type UserTrackerState = {
  [USER_CAMPAIGN_KEY]: string | undefined;
  [USER_MEDIUM_KEY]: string | undefined;
  [USER_SOURCE_KEY]: string | undefined;
  [USER_CONTENT_KEY]: string | undefined;
};

type UserTrackerAction = {
  payload: Partial<URLSearchParams>;
};

const userTrackersReducer = (
  state: UserTrackerState,
  action: UserTrackerAction
): UserTrackerState => {
  const newState = [...action.payload.entries()].reduce((acc, [key, value]) => {
    // Only map keys that exist in ParamToKey
    if (key in ParamToKey) {
      acc[ParamToKey[key as keyof typeof ParamToKey]] = value;
    }
    return acc;
  }, structuredClone(state));

  // Shallow comparison to avoid infinite loop
  // Return old state if values haven't changed
  const keys = Object.keys(newState) as Array<keyof UserTrackerState>;
  const isEqual = keys.every((key) => newState[key] === state[key]);

  return isEqual ? state : newState;
};

export const useAppTrackingProvider = () => {
  const [state, dispatch] = useReducer(
    userTrackersReducer,
    initialAppTrackingProviderContext.trackers
  );

  const setTrackers = useCallback(
    (trackers: URLSearchParams) => {
      // Use trackers.entries() - URLSearchParams is not a plain object
      for (const [key, value] of trackers.entries()) {
        if (value != null && key in ParamToKey) {
          // SSR-safe: Only access sessionStorage in browser environment
          if (typeof window !== "undefined") {
            sessionStorage.setItem(
              ParamToKey[key as keyof typeof ParamToKey],
              value
            );
          }
        }
      }
      dispatch({ payload: trackers });
    },
    [dispatch]
  );

  const urlTrackers = useUserTrackingProperties();
  useEffect(() => {
    if (urlTrackers != null) {
      setTrackers(urlTrackers);
    }
  }, [setTrackers, urlTrackers]);

  return useMemo(
    () => ({
      trackers: state,
      setTrackers,
    }),
    [state, setTrackers]
  );
};

const getInitialTrackers = (): UserTrackerState => {
  // SSR-safe: Only access sessionStorage in browser environment
  if (typeof window === "undefined") {
    return {
      [USER_SOURCE_KEY]: undefined,
      [USER_CAMPAIGN_KEY]: undefined,
      [USER_MEDIUM_KEY]: undefined,
      [USER_CONTENT_KEY]: undefined,
    };
  }
  return {
    [USER_SOURCE_KEY]: sessionStorage.getItem(USER_SOURCE_KEY) ?? undefined,
    [USER_CAMPAIGN_KEY]: sessionStorage.getItem(USER_CAMPAIGN_KEY) ?? undefined,
    [USER_MEDIUM_KEY]: sessionStorage.getItem(USER_MEDIUM_KEY) ?? undefined,
    [USER_CONTENT_KEY]: sessionStorage.getItem(USER_CONTENT_KEY) ?? undefined,
  };
};

export const initialAppTrackingProviderContext = {
  trackers: getInitialTrackers(),
  setTrackers: () => {
    throw new Error("AppTrackingProviderContext isn't initialized yet");
  },
};

export const AppTrackingContext = createContext<
  ReturnType<typeof useAppTrackingProvider>
>(initialAppTrackingProviderContext);
